import type { WootricConfig, WootricUser } from '../types/wootric';

interface WootricInitOptions {
  config: WootricConfig;
  user: WootricUser;
}

declare global {
  interface Window {
    wootricSettings?: {
      email: string;
      created_at: string;
      modal_theme: string;
      account_token: string;
      properties: {
        product: string;
        customerName: string;
        userRole: string;
        productReportingCode: string;
        deviceOS: string;
        link_url: string;
        link_word: string;
      };
      no_last_seen_cookie?: boolean;
    };
    wootric?: (action: string) => void;
    wootric_no_surveyed_cookie?: boolean;
    wootric_survey_immediately?: boolean;
  }
}

function getDeviceOS(): string {
  const userAgent = navigator.userAgent;

  if (/Windows/i.test(userAgent)) return 'Windows';
  if (/Macintosh|Mac OS X/i.test(userAgent)) return 'macOS';
  if (/Linux/i.test(userAgent)) return 'Linux';
  if (/Android/i.test(userAgent)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iOS';

  return 'Unknown';
}

function isEmailBlacklisted(email: string, blacklistedDomains: string[]): boolean {
  const emailDomain = email.split('@')[1]?.toLowerCase();
  if (!emailDomain) return false;

  return blacklistedDomains.some(
    (domain) =>
      emailDomain === domain.toLowerCase() || emailDomain.endsWith('.' + domain.toLowerCase()),
  );
}

export const wootricService = {
  start(options: WootricInitOptions): void {
    const { config, user } = options;

    if (config.wootricDomain !== 'Test' && config.wootricDomain !== 'Production') {
      console.log('Wootric: Disabled (wootricDomain not set to Test or Production)');
      return;
    }

    if (!user.email) {
      console.log('Wootric: No user email provided');
      return;
    }

    if (isEmailBlacklisted(user.email, config.blacklistedDomains)) {
      console.log('Wootric: Email domain is blacklisted');
      return;
    }

    const isTestMode = config.wootricDomain === 'Test';
    const accountId = isTestMode ? config.testAccountId : config.productionAccountId;

    if (isTestMode) {
      window.wootric_no_surveyed_cookie = true;
      window.wootric_survey_immediately = true;
    }

    const createdAtTimestamp = user.createdAt
      ? Math.floor(new Date(user.createdAt).getTime() / 1000).toString()
      : Math.floor(Date.now() / 1000).toString();

    window.wootricSettings = {
      email: user.email,
      created_at: createdAtTimestamp,
      modal_theme: 'Dark',
      account_token: accountId,
      properties: {
        product: config.product,
        customerName: user.organisationName ?? '',
        userRole: user.role || 'Unknown',
        productReportingCode: config.productReportingCode,
        deviceOS: getDeviceOS(),
        link_url: config.linkUrl,
        link_word: config.linkWord,
      },
    };

    this.loadScriptAndRun();
  },

  loadScriptAndRun(): void {
    if (document.getElementById('wootric-sdk')) {
      this.runWithRetry(0);
      return;
    }

    const script = document.createElement('script');
    script.id = 'wootric-sdk';
    script.src = 'https://cdn.wootric.com/wootric-sdk.js';
    script.async = true;
    script.onload = () => {
      this.runWithRetry(0);
    };
    script.onerror = () => {
      console.error('Wootric: Failed to load SDK');
    };

    document.head.appendChild(script);
  },

  runWithRetry(attempts: number): void {
    const maxAttempts = 10;
    const retryDelay = 1000;

    if (attempts >= maxAttempts) {
      console.warn('Wootric was unable to load');
      return;
    }

    if (!window.wootric) {
      setTimeout(() => {
        this.runWithRetry(attempts + 1);
      }, retryDelay);
      return;
    }

    window.wootric('run');
  },

  stop(): void {
    window.wootric_no_surveyed_cookie = true;
    window.wootricSettings = {
      no_last_seen_cookie: true,
    } as typeof window.wootricSettings;

    if (window.wootric) {
      window.wootric('stop');
    }
  },

  cleanup(): void {
    delete window.wootricSettings;
    delete window.wootric_no_surveyed_cookie;
    delete window.wootric_survey_immediately;
  },
};
